// src/components/sidebar/ConversationList.tsx
import React, { useState } from 'react';
import {
  Box,
  List,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { ConversationItem } from './ConversationItem';
import { Conversation } from '../../stores/conversationStore';
import { useT } from '../../i18n/useT';

interface ConversationListProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentConversationId,
  onSelectConversation,
}) => {
  const t = useT();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading] = useState(false);

  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation =>
    conversation.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group conversations by date
  const groupedConversations = React.useMemo(() => {
    const groups: { [key: string]: Conversation[] } = {};
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    filteredConversations.forEach(conversation => {
      const conversationDate = new Date(conversation.updated_at);
      let groupKey: string;

      if (conversationDate >= today) {
        groupKey = 'Today';
      } else if (conversationDate >= yesterday) {
        groupKey = 'Yesterday';
      } else if (conversationDate >= weekAgo) {
        groupKey = 'Previous 7 days';
      } else {
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        groupKey = `${monthNames[conversationDate.getMonth()]} ${conversationDate.getFullYear()}`;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(conversation);
    });

    // Sort groups by most recent first
    const sortedGroups: { [key: string]: Conversation[] } = {};
    const groupOrder = ['Today', 'Yesterday', 'Previous 7 days'];
    
    groupOrder.forEach(key => {
      if (groups[key]) {
        sortedGroups[key] = groups[key].sort((a, b) => 
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      }
    });

    // Add remaining groups (months)
    Object.keys(groups)
      .filter(key => !groupOrder.includes(key))
      .sort((a, b) => {
        const aDate = groups[a][0].updated_at;
        const bDate = groups[b][0].updated_at;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      })
      .forEach(key => {
        sortedGroups[key] = groups[key].sort((a, b) => 
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      });

    return sortedGroups;
  }, [filteredConversations]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t("Search conversations...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : Object.keys(groupedConversations).length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedConversations).map(([groupName, groupConversations]) => (
            <Box key={groupName}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  px: 2,
                  py: 1,
                  display: 'block',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {groupName}
              </Typography>
              <List dense sx={{ py: 0 }}>
                {groupConversations.map((conversation) => (
                  <ConversationItem
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => onSelectConversation(conversation.id)}
                    isActive={currentConversationId === conversation.id}
                  />
                ))}
              </List>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};